import { Tag, Tags } from '../TagsList/types';

export interface PacksListProps {
	packFeedName?: string;
	packClassName?: string;
	activeTag?: Tag;
	packs: Packs;
	tags?: Tags;
	isLoading: boolean;
	showGreeting?: boolean;
	showTitle?: boolean;
	hideUpsellBanner?: boolean;
	isClassPage?: boolean;
}

export interface ApiAsset {
	content_type: string;
	dominant_colors?: string[];
	download_url?: string;
	height?: number;
	id: string;
	size: number;
	type?: 'audio' | 'video' | 'captions' | 'image';
	url: string;
	width?: number;
	duration?: number;
	orientation?: 'landscape' | 'portrait';
	updated_at?: string;
}

export interface ProgramOrGuideItem {
	guide_id?: string;
	program_id: string;
	is_new: boolean;
	is_unlocked: boolean;
	duration: number;
	type: string;
	image: ApiAsset;
	title: string;
	subtitle: string;
	narrator_name: string;
	description: string;
	content_type: string;
	promotional_text: string;
	full_subtitle?: string;
	search_category?: string;
	asset_type?: 'audio' | 'video' | string;
	detail_image?: ApiAsset;
	release_date?: string;
	// Editorial badge copy (e.g. "New & Interactive") shown in place of the plain "New" badge.
	tag?: string;
}

export interface CollectionItem {
	feed_id: string;
	is_new: boolean;
	is_unlocked: boolean;
	search_category?: string;
	type: string;
	image: ApiAsset;
	title: string;
	promotional_text: string;
	onclick_override?: () => Promise<void>; // Left ambiguous, but this is a custom type to allow b2b to link to external content
	show_your_plan?: boolean; // Another b2b override - shows "your plan" at the top left of the tile
}

export interface NarratorItem {
	narrator_id: string;
	is_new: boolean;
	is_unlocked: boolean;
	search_category?: string;
	content_type?: string;
	type: string;
	image: ApiAsset;
	title: string;
}

export interface UrlItem {
	type: string;
	url: string;
	// Absent on the tout/banner-promo url item the api sends for seasonal promos — those
	// packs carry no lock/new state of their own, unlike a program- or guide-backed url item.
	is_new?: boolean;
	is_unlocked?: boolean;
	image?: ApiAsset;
	// Absent on the tout/banner-promo url item when the api sends no copy for it — ToutPack
	// then falls back to the pack's own title, and a pack with no title of its own renders
	// nothing at all.
	title?: string;
	narrator_name?: string;
	description?: string;
	show_your_plan?: boolean; // Another b2b override - shows "your plan" at the top left of the tile
	// Only present on tout/banner-promo url items; every other url item leaves this unset.
	subtitle?: string;
	id?: string;
}

export interface TextItem {
	text: string;
	is_new: boolean;
	is_unlocked: boolean;
	type: 'text';
}

export interface PackItem {
	type: string;
	title: string;
	subtitle?: string;
	promotional_text?: string;
	image: ApiAsset;
	pack_class: string;
	is_unlocked?: boolean;
	is_new?: boolean;
	tag?: string;
}

export type SinglePackItem =
	CollectionItem | UrlItem | ProgramOrGuideItem | TextItem | PackItem | NarratorItem;

export type PackCardItem = CollectionItem | UrlItem | ProgramOrGuideItem | PackItem | NarratorItem;

export interface PackAssociation {
	image?: ApiAsset;
	title: string;
	narrator_id?: string;
}

export interface Pack {
	id: string;
	items: Array<SinglePackItem | CollectionItem | UrlItem>;
	pack_class: string;
	content_description: string;
	display_type: string;
	evaluation_type: string;
	has_more?: boolean;
	title?: string;
	promotional_text?: string;
	content_type?: string;
	description?: string;
	narrator_name?: string;
	subtitle?: string;
	// The rest of the fields below are only sent for `pack_type: 'playlist'` packs
	// (display_type: 'row') and are absent on every other pack shape.
	pack_type?: string;
	image?: ApiAsset;
	is_faveable?: boolean;
	associations?: PackAssociation[];
	total_item_number?: number;
	release_date?: string;
}

export function isProgramOrGuide(packItem: SinglePackItem): packItem is ProgramOrGuideItem {
	return (packItem as ProgramOrGuideItem).program_id !== undefined;
}

// Shared between search results and every browse grid CardGrid renders (home, feeds,
// class pages) — CardGrid's render filter and any empty-state check derived from it must
// both call this, or they can disagree about whether a result set is actually empty.
// Collections reach this only because the search service happens to return them today;
// admitting a type here makes it renderable and clickable on ALL of those surfaces, not
// just search. Widen with that in mind.
export type RenderablePackItem = ProgramOrGuideItem | PackItem | CollectionItem | NarratorItem;

export function isRenderablePackItem(packItem: SinglePackItem): packItem is RenderablePackItem {
	return (
		packItem.type === 'program' ||
		packItem.type === 'guide' ||
		packItem.type === 'pack' ||
		isCollection(packItem) ||
		isNarrator(packItem)
	);
}

export function isCollection(packItem: SinglePackItem): packItem is CollectionItem {
	return (packItem as CollectionItem).feed_id !== undefined;
}

export function isNarrator(packItem: SinglePackItem): packItem is NarratorItem {
	return (packItem as NarratorItem).narrator_id !== undefined;
}

export function isUrl(items: SinglePackItem): items is UrlItem {
	return (items as UrlItem)?.url !== undefined;
}

export function isCollectionArray(
	items: Array<CollectionItem | SinglePackItem | UrlItem>,
): items is CollectionItem[] {
	return (items as CollectionItem[])?.[0]?.feed_id !== undefined;
}

export type Packs = Pack[];
